// src/pages/PublicScratchCardPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Gift, Sparkles, AlertCircle, Loader2, Lock } from 'lucide-react'; // ⭐ Added Lock icon
import { toast } from 'react-toastify';
import api from '../utils/api'; 
import { ICON_MAP, COLORS } from '../components/discounts/ScratchCardManager'; 
import ClaimPrizeForm from '../components/public/ClaimPrizeForm'; 

// --- HELPER: Update Meta Tags for SEO & Social Sharing ---
const updateMetaTags = (game) => {
    if (!game) return;

    const title = game.seoTitle || game.name || 'Scratch & Win!';
    const description = game.seoDescription || `Play our scratch card game to reveal exclusive prizes from ${game.companyName || 'us'}!`;
    const image = game.mainPhotoUrl || game.companyLogoUrl || ''; 

    // 1. Update Browser Title
    document.title = title;

    // 2. Helper to update or create a meta tag
    const setMeta = (name, content, attribute = 'name') => {
        let element = document.querySelector(`meta[${attribute}="${name}"]`);
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute(attribute, name);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    };

    // 3. Standard Description
    setMeta('description', description);

    // 4. Open Graph 
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'website', 'property');
    if (image) setMeta('og:image', image, 'property');

    // 5. Twitter Card
    setMeta('twitter:card', 'summary_large_image', 'name');
    setMeta('twitter:title', title, 'name');
    setMeta('twitter:description', description, 'name');
    if (image) setMeta('twitter:image', image, 'name');

    // 6. Favicon
    if (game.faviconUrl) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = game.faviconUrl;
    }
};

// --- SUB-COMPONENT: SCRATCH CARD DISPLAY LOGIC ---

const SCRATCH_THRESHOLD = 60; // % required to unlock reveal

const PublicScratchCardDisplay = ({ gameData }) => {
    const [prize, setPrize] = useState(null);
    const [isRevealed, setIsRevealed] = useState(false);
    const [isPlayed, setIsPlayed] = useState(false); 
    // ⭐ NEW: Track scratch progress
    const [scratchPercentage, setScratchPercentage] = useState(0);
    
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isScratching, setIsScratching] = useState(false);
    
    // --- Core Game Logic ---
    const determinePrize = useCallback((prizes) => {
        const rand = Math.random() * 100;
        let cumulativeProbability = 0;
        
        for (const p of prizes) {
            cumulativeProbability += p.probability;
            if (rand <= cumulativeProbability) {
                return p;
            }
        }
        return prizes[prizes.length - 1]; 
    }, []);

    // Browser Lock Implementation using LocalStorage
    useEffect(() => {
        if (!gameData || !gameData.prizes) return;

        const storageKey = `scratch_game_result_${gameData._id}`;
        const storedResult = localStorage.getItem(storageKey);

        if (storedResult) {
            try {
                const parsed = JSON.parse(storedResult);
                
                const assignedPrize = gameData.prizes.find(p => 
                    (p._id && p._id === parsed.prizeId) || 
                    (p.id && p.id === parsed.prizeId)
                );

                if (assignedPrize) {
                    setPrize(assignedPrize);
                    setIsPlayed(true); 
                    
                    if (parsed.isRevealed) {
                        setIsRevealed(true);
                        setScratchPercentage(100); // Fully scratched if already revealed
                    }
                }
            } catch (e) {
                console.error("Error parsing stored game data", e);
            }
        } else if (!prize) {
            const determinedPrize = determinePrize(gameData.prizes);
            setPrize(determinedPrize);
            
            localStorage.setItem(storageKey, JSON.stringify({
                prizeId: determinedPrize._id || determinedPrize.id,
                isRevealed: false, 
                timestamp: Date.now()
            }));
        }
    }, [gameData, prize, determinePrize]);


    // --- Canvas Initialization and Drawing ---
    const drawScratchLayer = useCallback((canvas, ctx, container) => {
        const width = container.offsetWidth;
        const height = container.offsetHeight;
        
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        // 1. Draw Silver Scratch Layer
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#C0C0C0');
        gradient.addColorStop(0.5, '#E0E0E0');
        gradient.addColorStop(1, '#C0C0C0');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        
        // 2. Add "Noise" texture for realism
        for (let i = 0; i < 600; i++) {
            ctx.fillStyle = Math.random() > 0.5 ? '#A9A9A9' : '#F5F5F5';
            ctx.fillRect(Math.random() * width, Math.random() * height, 2, 2);
        }

        // 3. Add Label Text
        ctx.font = 'bold 22px sans-serif';
        ctx.fillStyle = '#64748b'; // slate-500
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('SCRATCH HERE', width / 2, height / 2);
        
        // 4. Add a little sparkle icon
        ctx.font = '26px serif';
        ctx.fillText('✨', width / 2, (height / 2) - 35);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        const container = containerRef.current;

        // Only draw if the prize is ready and we haven't revealed it yet
        if (prize && canvas && container && !isRevealed) {
            drawScratchLayer(canvas, ctx, container);
        }
    }, [isRevealed, prize, drawScratchLayer]);

    const updateStorageRevealed = () => {
        if (!prize || !gameData) return;
        const storageKey = `scratch_game_result_${gameData._id}`;
        
        localStorage.setItem(storageKey, JSON.stringify({
            prizeId: prize._id || prize.id,
            isRevealed: true,
            timestamp: Date.now()
        }));
    };

    // ⭐ NEW: Function to calculate transparency percentage
    const calculateScratchPercentage = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return 0;

        const width = canvas.width;
        const height = canvas.height;
        
        try {
            // Get pixel data
            const imageData = ctx.getImageData(0, 0, width, height);
            const pixels = imageData.data;
            let transparentPixels = 0;
            
            // Optimization: Check every 32nd pixel (stride) to avoid lag on large screens
            const stride = 32; 
            let sampledPixels = 0;

            for (let i = 0; i < pixels.length; i += stride * 4) {
                // Check alpha channel (4th byte)
                // If alpha is less than 128 (half transparent), count it
                if (pixels[i + 3] < 128) {
                    transparentPixels++;
                }
                sampledPixels++;
            }
            
            return Math.round((transparentPixels / sampledPixels) * 100);
        } catch (e) {
            console.error("Error calculating scratch percentage", e);
            return 0;
        }
    }, []);

    // Scratch Logic
    const scratch = useCallback((x, y) => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        const offsetX = (x - rect.left) * scaleX;
        const offsetY = (y - rect.top) * scaleY;

        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(offsetX / (window.devicePixelRatio||1), offsetY / (window.devicePixelRatio||1), 25, 0, Math.PI * 2);
        ctx.fill();
        
    }, []);

    const handleInteract = (e) => {
        if (isRevealed || !prize) return; 
        
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        if (e.type === 'mousedown' || e.type === 'touchstart') setIsScratching(true);
        
        if (isScratching || e.type === 'mousedown' || e.type === 'touchstart') {
            scratch(clientX, clientY);
        }
    };

    const handleMouseUpOrTouchEnd = () => {
        if (isScratching && !isRevealed) {
            setIsScratching(false);
            if (!isPlayed) setIsPlayed(true);

            // ⭐ Calculate percentage when user stops scratching
            const pct = calculateScratchPercentage();
            setScratchPercentage(pct);
        }
    };
    
    const handleForceReveal = () => {
        if (!prize) return;
        setIsRevealed(true);
        setIsPlayed(true);
        setScratchPercentage(100);
        updateStorageRevealed(); 
    };
    
    const IconComponent = ICON_MAP[prize?.icon] || Gift;
    const colorTheme = COLORS.find(c => c.name === prize?.color) || COLORS[0];
    
    const canReveal = scratchPercentage >= SCRATCH_THRESHOLD;

    // --- Render Card or Claim Form ---
    return (
        <div className="flex flex-col items-center w-full">
            {isRevealed && prize ? (
                <ClaimPrizeForm 
                    prize={prize} 
                    gameId={gameData._id}
                    prizeId={prize._id} 
                    onClaimSuccess={() => console.log('Lead created.')}
                />
            ) : (
                <>
                    {/* ⭐ AESTHETIC UPGRADE: Sleek header inside the card */}
                    <h1 className="text-3xl font-black tracking-tight mb-8 text-slate-900 dark:text-white text-center">
                         {gameData.name || 'Scratch Card Game'}
                    </h1>
                    
                   <div 
                    ref={containerRef}
                    // ⭐ AESTHETIC UPGRADE: Larger 72x72 container, rounded-3xl, deep shadow, frosted borders
                    className="relative w-72 h-72 rounded-3xl shadow-2xl overflow-hidden border-4 border-white/50 dark:border-slate-800 select-none bg-slate-50 dark:bg-slate-800/50 ring-4 ring-indigo-500/20"
                    onMouseDown={handleInteract}
                    onMouseMove={handleInteract}
                    onMouseUp={handleMouseUpOrTouchEnd}
                    onMouseLeave={handleMouseUpOrTouchEnd}
                    onTouchStart={handleInteract}
                    onTouchMove={handleInteract}
                    onTouchEnd={handleMouseUpOrTouchEnd}
                  >
                    {/* HIDDEN PRIZE CONTENT */}
                    <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center transition-all duration-300`}>
                        <div className={`p-5 rounded-full mb-4 shadow-inner transition-transform duration-500 ${colorTheme.bg}`}>
                            <IconComponent className={`w-14 h-14 ${colorTheme.text}`} />
                        </div>
                        <h4 className="text-2xl font-black text-slate-900 dark:text-white leading-tight mb-2 tracking-tight drop-shadow-sm">{prize?.title || 'Mystery Prize'}</h4>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-snug max-w-[80%] mx-auto">{prize?.description || 'Scratch to reveal your reward'}</p>
                    </div>

                    {/* OVERLAY CANVAS */}
                    <canvas
                      ref={canvasRef}
                      className={`absolute inset-0 touch-none cursor-crosshair transition-opacity duration-700 ${isRevealed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                    />
                  </div>
                  
                  {/* ⭐ AESTHETIC UPGRADE: Chunky modern button with scale interaction */}
                  <button 
                        onClick={handleForceReveal} 
                        disabled={isRevealed || !prize || !canReveal}
                        className={`mt-10 w-full max-w-sm px-6 py-4 font-black text-lg rounded-2xl shadow-xl transition-all duration-300 flex items-center justify-center
                            ${canReveal 
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white hover:scale-[1.02] active:scale-[0.98] shadow-indigo-500/20' 
                                : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed opacity-80'
                            }
                        `}
                     >
                        {!canReveal && <Lock className="w-5 h-5 mr-3 opacity-70" />}
                        {isPlayed 
                            ? (canReveal ? 'Reveal My Prize' : `Keep Scratching! (${scratchPercentage}%)`) 
                            : 'Scratch to Play'}
                  </button>
                  
                  {!canReveal && isPlayed && (
                      <p className="text-sm font-medium text-slate-400 dark:text-slate-500 mt-4 animate-pulse">
                          Scratch at least {SCRATCH_THRESHOLD}% of the card to reveal your prize.
                      </p>
                  )}
                </>
            )}
        </div>
    );
};


// --- MAIN PAGE COMPONENT ---
const PublicScratchCardPage = () => {
    const { slug } = useParams();
    
    const { data: game, isLoading, isError, error } = useQuery({
        queryKey: ['publicScratchCard', slug],
        queryFn: async () => {
            const res = await api.get(`/public/scratch-card/${slug}`);
            return {
                ...res.data.game,
                companyName: res.data.companyName, 
                companyLogoUrl: res.data.companyLogoUrl, 
            };
        },
        enabled: !!slug, 
        staleTime: Infinity, 
        cacheTime: Infinity,
    });

    // Effect to Update SEO and Favicon
    useEffect(() => {
        if (game) {
            updateMetaTags(game);
        }
        
        // Cleanup to reset title on unmount
        return () => {
            document.title = 'ServiceOS'; 
        };
    }, [game]);
    
    const PageHeader = ({ title, logoUrl, companyName }) => {
        return (
            // ⭐ AESTHETIC UPGRADE: Blurred sticky header to match new design system
            <header className="bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50 shadow-sm sticky top-0 z-20">
                <div className="max-w-xl mx-auto p-4 flex items-center justify-center sm:justify-start">
                    {logoUrl ? (
                         <img src={logoUrl} alt="Company Logo" className="h-10 w-auto mr-4 drop-shadow-sm" />
                    ) : (
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mr-3">
                            <Gift className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        </div>
                    )}
                    <h1 className="text-xl font-black tracking-tight text-slate-800 dark:text-white truncate max-w-[200px] sm:max-w-xs">{title || companyName || 'Special Offer'}</h1>
                </div>
            </header>
        );
    };

    const displayMessage = (icon, title, message) => (
        // ⭐ AESTHETIC UPGRADE: Modal-style error display with glassmorphism
        <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 sm:p-10 text-center border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-500">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                {React.cloneElement(icon, { className: "w-8 h-8 text-slate-500 dark:text-slate-400" })}
            </div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">{title}</h1>
            <p className="text-base font-medium text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">{message}</p>
            <a href="/" className="inline-block w-full px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                Return to Homepage
            </a>
        </div>
    );

    if (isLoading) {
        return (
            // ⭐ AESTHETIC UPGRADE: Slate background for loading state
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
                <span className="text-lg font-bold tracking-tight text-slate-600 dark:text-slate-400 animate-pulse">Loading Game...</span>
            </div>
        );
    }
    
    if (isError || !game) {
        const errorMessage = error?.response?.data?.message || 'The scratch card game you are looking for could not be found or is no longer active.';
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
                {displayMessage(
                    <AlertCircle className="text-red-500" />,
                    'Game Not Found',
                    errorMessage
                )}
            </div>
        );
    }
    
    return (
        // ⭐ AESTHETIC UPGRADE: Main wrapper with slate backgrounds
        <div className="bg-slate-50 dark:bg-slate-950 min-h-screen font-sans flex flex-col">
            <PageHeader 
                title={game.name || 'Special Offer'}
                logoUrl={game.companyLogoUrl} 
                companyName={game.companyName}
            />
            <main className="flex-1 p-4 sm:p-8 w-full max-w-2xl mx-auto flex flex-col justify-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* ⭐ AESTHETIC UPGRADE: Deeply rounded, glassmorphism container */}
                <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-6 sm:p-10 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800">
                    
                    {/* Main Hero Photo */}
                    {game.mainPhotoUrl && (
                        <div className="mb-10 -mx-6 -mt-6 sm:-mx-10 sm:-mt-10 rounded-t-3xl overflow-hidden h-56 md:h-72 relative">
                            <img 
                                src={game.mainPhotoUrl} 
                                alt="Campaign" 
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end p-6 sm:p-8">
                                <h2 className="text-3xl md:text-4xl font-black text-white drop-shadow-lg tracking-tight">
                                    You've Got a Chance to Win!
                                </h2>
                            </div>
                        </div>
                    )}

                    {/* Default Header if no photo */}
                    {!game.mainPhotoUrl && (
                        <div className="text-center mb-10">
                            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-indigo-600 dark:text-indigo-400 mb-3 drop-shadow-sm">You've Got a Chance to Win!</h2>
                            <p className="text-base font-medium text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                                Scratch the card below to instantly reveal your exclusive offer from {game.companyName || 'the Company'}.
                            </p>
                        </div>
                    )}
                    
                    {/* Description if photo exists */}
                    {game.mainPhotoUrl && (
                        <p className="text-center text-base font-medium text-slate-600 dark:text-slate-400 mb-10 max-w-md mx-auto leading-relaxed">
                            Scratch the card below to instantly reveal your exclusive offer from {game.companyName || 'the Company'}.
                        </p>
                    )}

                    <PublicScratchCardDisplay gameData={game} />
                </div>
            </main>
        </div>
    );
};

export default PublicScratchCardPage;